import {
  addDoc,
  collection,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';

export type PaymentMethod =
  | 'bank_transfer'
  | 'mobile_money'
  | 'cash'
  | 'check'
  | 'other';
export type PaymentStatus = 'pending' | 'paid';

export interface PaymentRow {
  id: string;
  activityId?: string;
  paidById?: string;
  method?: PaymentMethod;
  amount?: number;
  status?: PaymentStatus;
  createdAt?: Date;
  updatedAt?: Date;
}

const COLLECTION = 'payments';
const PAYMENT_METHODS: PaymentMethod[] = [
  'bank_transfer',
  'mobile_money',
  'cash',
  'check',
  'other',
];

function parsePaymentMethod(value: unknown): PaymentMethod | undefined {
  if (typeof value !== 'string') return undefined;
  return PAYMENT_METHODS.includes(value as PaymentMethod) ? (value as PaymentMethod) : undefined;
}

function parsePaymentStatus(value: unknown): PaymentStatus | undefined {
  if (value === 'pending' || value === 'paid') return value;
  return undefined;
}

export async function listPayments(options?: {
  status?: PaymentStatus;
  limitCount?: number;
}): Promise<PaymentRow[]> {
  const clauses: any[] = [];
  if (options?.status) {
    clauses.push(where('status', '==', options.status));
  }
  clauses.push(orderBy('createdAt', 'desc'));
  if (options?.limitCount) {
    clauses.push(limit(options.limitCount));
  }

  const snap = await getDocs(
    query(collection(db, COLLECTION), ...clauses)
  );

  return snap.docs.map((d) => {
    const data = d.data() as any;
    return {
      id: d.id,
      activityId: data.activityId,
      paidById: data.paidById,
      method: parsePaymentMethod(data.method),
      amount:
        typeof data.amount === 'number'
          ? data.amount
          : typeof data.amount === 'string'
          ? Number(data.amount)
          : undefined,
      status: parsePaymentStatus(data.status),
      createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : undefined,
      updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : undefined,
    };
  });
}

export async function markPaymentAsPaid(id: string): Promise<void> {
  const ref = doc(db, COLLECTION, id);
  await updateDoc(ref, { status: 'paid' });
}

export async function updatePayment(
  id: string,
  payload: {
    activityId: string;
    paidById: string;
    method: PaymentMethod;
    amount: number;
    status: PaymentStatus;
  }
): Promise<void> {
  const ref = doc(db, COLLECTION, id);
  await updateDoc(ref, {
    activityId: payload.activityId.trim(),
    paidById: payload.paidById.trim(),
    method: payload.method.trim(),
    amount: payload.amount,
    status: payload.status,
    updatedAt: new Date(),
  });
}

export async function createPayment(payload: {
  activityId: string;
  paidById: string;
  method: PaymentMethod;
  amount: number;
  status: PaymentStatus;
}): Promise<string> {
  const ref = await addDoc(collection(db, COLLECTION), {
    activityId: payload.activityId.trim(),
    paidById: payload.paidById.trim(),
    method: payload.method.trim(),
    amount: payload.amount,
    status: payload.status,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

